// Orbis · Studio · top-level App
//
// Three audiences on one domain:
//   /              public landing (no auth)
//   /work/:slug    public case study (no auth)
//   /login         magic-link request (no auth)
//   /login/verify  magic-link verify (consumes token via worker redirect)
//   /app/*         brand portal (auth: role=brand, magic-link cookie)
//   /operator/*    operator dashboard (auth: role=operator via admin SSO)
//
// Operator routes that used to live at root (/campaigns, /operations, etc.)
// moved under /operator/* to make room for the public landing. A /campaigns
// hit from a stale bookmark falls through to the public Landing route.

import { Routes, Route, Navigate } from 'react-router-dom';

import { Hub } from './routes/public/Hub';
import { WorkDetail } from './routes/public/WorkDetail';
import { Inquire } from './routes/public/Inquire';
import { Qualify } from './routes/public/Qualify';
import { Invite } from './routes/public/Invite';
import { LoginRequest } from './routes/login/LoginRequest';
import { LoginVerify } from './routes/login/LoginVerify';

import { AppShell } from './components/app/AppShell';
import { Home as BrandHome } from './routes/app/Home';
import { Brief } from './routes/app/Brief';
import { CampaignDetail as BrandCampaignDetail } from './routes/app/CampaignDetail';
import { Welcome as BrandWelcome } from './routes/app/Welcome';
import { Payment as BrandPayment } from './routes/app/Payment';
import { Clipper, ClipperVerify } from './routes/clipper/Clipper';
import { OperatorLogin } from './routes/operator/OperatorLogin';
import { OperatorVerify } from './routes/operator/OperatorVerify';

import Layout from './components/Layout';
import Dashboard from './routes/Dashboard';
import Operations from './routes/Operations';
import Relationships from './routes/Relationships';
import Campaigns from './routes/Campaigns';
import Brands from './routes/Brands';
import Audit from './routes/Audit';
import Agents from './routes/Agents';
import Verification from './routes/Verification';
import Distribution from './routes/Distribution';
import Settings from './routes/Settings';
import { lazy, Suspense } from 'react';
// Producer (video script boards) — lazy so the canvas library only loads on this tab.
const ProducerBoards = lazy(() => import('./routes/producer/ProducerBoards'));
const ProducerBoard = lazy(() => import('./routes/producer/ProducerBoard'));

function OperatorShell({ children }: { children: React.ReactNode }) {
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <Routes>
      {/* Public — one unified Studio home; /clipping + /localization deep-link into it */}
      <Route path="/" element={<Hub />} />
      <Route path="/clipping" element={<Navigate to="/#clipping" replace />} />
      <Route path="/localization" element={<Navigate to="/#localization" replace />} />
      <Route path="/work/:slug" element={<WorkDetail />} />
      <Route path="/inquire" element={<Inquire />} />
      <Route path="/qualify" element={<Qualify />} />
      <Route path="/invite/:token" element={<Invite />} />

      {/* Login */}
      <Route path="/login" element={<LoginRequest />} />
      <Route path="/login/verify" element={<LoginVerify />} />

      {/* Creator portal */}
      <Route path="/clipper" element={<Clipper />} />
      <Route path="/clipper/verify" element={<ClipperVerify />} />

      {/* Brand portal */}
      <Route path="/app/welcome" element={<BrandWelcome />} />
      <Route path="/app" element={<AppShell><BrandHome /></AppShell>} />
      <Route path="/app/brief" element={<AppShell><Brief /></AppShell>} />
      <Route path="/app/payment" element={<AppShell><BrandPayment /></AppShell>} />
      <Route path="/app/campaigns/:id" element={<AppShell><BrandCampaignDetail /></AppShell>} />

      {/* Operator auth (magic-link) */}
      <Route path="/operator/login" element={<OperatorLogin />} />
      <Route path="/operator/verify" element={<OperatorVerify />} />

      {/* Operator (moved under /operator/*) */}
      <Route path="/operator" element={<OperatorShell><Dashboard /></OperatorShell>} />
      <Route path="/operator/operations/*" element={<OperatorShell><Operations /></OperatorShell>} />
      <Route path="/operator/relationships/*" element={<OperatorShell><Relationships /></OperatorShell>} />
      <Route path="/operator/campaigns/*" element={<OperatorShell><Campaigns /></OperatorShell>} />
      <Route path="/operator/brands" element={<OperatorShell><Brands /></OperatorShell>} />
      <Route path="/operator/audit" element={<OperatorShell><Audit /></OperatorShell>} />
      <Route path="/operator/agents" element={<OperatorShell><Agents /></OperatorShell>} />
      <Route path="/operator/verification" element={<OperatorShell><Verification /></OperatorShell>} />
      <Route path="/operator/distribution/*" element={<OperatorShell><Distribution /></OperatorShell>} />
      <Route path="/operator/settings" element={<OperatorShell><Settings /></OperatorShell>} />
      <Route path="/operator/producer" element={<OperatorShell><Suspense fallback={null}><ProducerBoards /></Suspense></OperatorShell>} />
      <Route path="/operator/producer/:boardId" element={<OperatorShell><Suspense fallback={null}><ProducerBoard /></Suspense></OperatorShell>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
